import { supabase } from '@/lib/supabase';
import { DEFAULT_THEME_PAGES, buildPageDef, type ThemePageDef } from '@/lib/themePageDefs';

// ============================================================
// Fichiers de référence de thèmes (bibliothèque autonome).
// Chaque fichier de référence porte un titre, une description et
// un contenu (les instructions transmises à l'IA + éventuellement
// la liste des pages attendues).
// ============================================================

export interface ThemeReferenceFileRecord {
  id: number;
  titre: string;
  description: string;
  contenu: string;
  created_at: string;
}

export async function fetchThemeReferenceFiles(): Promise<ThemeReferenceFileRecord[]> {
  const { data, error } = await supabase
    .from('theme_reference_files')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []) as ThemeReferenceFileRecord[];
}

export async function fetchThemeReferenceFile(id: number): Promise<ThemeReferenceFileRecord | null> {
  const { data, error } = await supabase
    .from('theme_reference_files')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ThemeReferenceFileRecord) || null;
}

export async function createThemeReferenceFile(input: {
  titre: string;
  description: string;
  contenu: string;
}): Promise<ThemeReferenceFileRecord> {
  const titre = input.titre.trim();
  if (!titre) throw new Error('Le titre du fichier de référence est obligatoire.');
  const { data, error } = await supabase
    .from('theme_reference_files')
    .insert({
      titre,
      description: input.description.trim(),
      contenu: input.contenu,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as ThemeReferenceFileRecord;
}

export async function updateThemeReferenceFile(
  id: number,
  input: { titre: string; description: string; contenu: string },
): Promise<void> {
  const titre = input.titre.trim();
  if (!titre) throw new Error('Le titre du fichier de référence est obligatoire.');
  const { error } = await supabase
    .from('theme_reference_files')
    .update({ titre, description: input.description.trim(), contenu: input.contenu })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteThemeReferenceFile(id: number): Promise<void> {
  const { error } = await supabase.from('theme_reference_files').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// Les pages « structurelles » toujours nécessaires au bon fonctionnement d'un
// thème (squelette du site). On les ajoute si le fichier de référence ne les
// mentionne pas explicitement.
const LAYOUT_PAGE_KEYS = ['head', 'header', 'navmenu', 'footer'];

/** Alias entre les clés utilisées dans les fichiers de référence et les clés Zifek. */
const PAGE_KEY_ALIASES: Record<string, string> = {
  home: 'home', accueil: 'home',
  products: 'produits', produits: 'produits', shop: 'produits', boutique: 'produits',
  'product-detail': 'detailsproduit', product_detail: 'detailsproduit', product: 'detailsproduit', detailsproduit: 'detailsproduit',
  services: 'service', service: 'service',
  'service-detail': 'detailsservice', service_detail: 'detailsservice', detailsservice: 'detailsservice',
  cart: 'panier', panier: 'panier',
  signup: 'creationcompte', register: 'creationcompte', inscription: 'creationcompte', creationcompte: 'creationcompte',
  login: 'connexion', connexion: 'connexion',
  'my-account': 'compte', account: 'compte', compte: 'compte', 'mon-compte': 'compte',
  about: 'apropos', apropos: 'apropos',
  team: 'equipe', equipe: 'equipe',
  portfolio: 'project', project: 'project',
  blog: 'blog',
  article: 'detailsblog', 'blog-detail': 'detailsblog', detailsblog: 'detailsblog',
  contact: 'contact',
  reviews: 'avis', avis: 'avis', temoignages: 'avis',
  privacy: 'politique', politique: 'politique',
  terms: 'conditionsdutilisation', conditions: 'conditionsdutilisation', conditionsdutilisation: 'conditionsdutilisation',
  faq: 'faq',
  pricing: 'tarifs', tarifs: 'tarifs',
  gallery: 'gallery', galerie: 'gallery',
  booking: 'booking', reservation: 'booking',
  head: 'head', header: 'header', navmenu: 'navmenu', footer: 'footer',
};

function normalizePageKey(raw: string): string {
  const key = (raw || '').trim().toLowerCase();
  return PAGE_KEY_ALIASES[key] || key;
}

/**
 * Extrait la liste des pages attendues depuis le contenu du fichier de
 * référence. Trois formats sont reconnus, par ordre de priorité :
 *
 *  1. Format officiel des fichiers Zifek :
 *        8. PAGE ACCUEIL
 *        ===============
 *        PAGE KEY :
 *        home
 *     La clé suit la ligne « PAGE KEY : », le libellé vient du titre
 *     « N. PAGE LIBELLÉ » qui le précède.
 *  2. Liste simple : `- clé : Libellé` (ou `clé - Libellé`) sous un titre
 *     contenant « Pages ».
 *  3. À défaut, on retombe sur la liste complète par défaut.
 */
export function parseReferencePages(contenu: string): { key: string; label: string }[] {
  const text = (contenu || '').replace(/\r\n?/g, '\n');
  const pages: { key: string; label: string }[] = [];
  const seen = new Set<string>();

  // ── Format 1 : « N. PAGE LIBELLÉ » + « PAGE KEY : » <clé> ──
  const headings = Array.from(text.matchAll(/^\s*\d+\.\s*PAGE\s+(.+?)\s*$/gim)).map((m) => ({
    index: m.index ?? 0,
    label: m[1].replace(/^PAGE\s+/i, '').trim(),
  }));
  const keyMatches = Array.from(text.matchAll(/PAGE\s*KEY\s*:?\s*\n+\s*([A-Za-z0-9_-]{2,40})/gi));
  if (keyMatches.length > 0) {
    for (const km of keyMatches) {
      const rawKey = km[1];
      const key = normalizePageKey(rawKey);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      // Libellé = dernier titre « PAGE ... » situé avant la clé.
      const before = headings.filter((h) => h.index < (km.index ?? 0));
      const label = before.length > 0 ? before[before.length - 1].label : key;
      pages.push({ key, label: label || key });
    }
    if (pages.length > 0) return withLayoutPages(pages);
  }

  // ── Format 2 : liste `- clé : Libellé` sous une section « Pages » ──
  const lines = text.split('\n');
  let inPages = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const heading = line.match(/^#{1,6}\s*(.+)$/);
    if (heading) {
      inPages = /page/i.test(heading[1]);
      continue;
    }
    if (/^pages?\s*:?\s*$/i.test(line)) { inPages = true; continue; }
    if (!inPages) continue;
    const m = line.match(/^[-*•\d.]*\s*([a-zA-Z0-9][a-zA-Z0-9_-]{1,40})\s*[:\-–]\s*(.+)$/);
    if (m) {
      const key = normalizePageKey(m[1]);
      const label = m[2].trim();
      if (key && label && !seen.has(key)) { seen.add(key); pages.push({ key, label }); }
    }
  }
  if (pages.length > 0) return withLayoutPages(pages);

  return DEFAULT_THEME_PAGES;
}

/** Ajoute les pages de layout (head/header/navmenu/footer) si absentes. */
function withLayoutPages(pages: { key: string; label: string }[]): { key: string; label: string }[] {
  const out = [...pages];
  const keys = new Set(out.map((p) => p.key));
  for (const k of LAYOUT_PAGE_KEYS) {
    if (!keys.has(k)) out.push({ key: k, label: k });
  }
  return out;
}

/**
 * Retourne les définitions de pages d'un fichier de référence.
 * Si le contenu ne définit aucune page, on retombe sur la liste complète
 * par défaut pour ne jamais laisser un thème sans pages.
 */
export function getReferencePages(contenu: string): ThemePageDef[] {
  const parsed = parseReferencePages(contenu);
  if (parsed.length === 0) return DEFAULT_THEME_PAGES;
  return parsed.map((p) => buildPageDef(p.key, p.label));
}